import React, { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import styled, { useTheme } from 'styled-components'
import { Flex, Text, TextVariant } from '@mprolab/uikit'
import MainWrapper from 'layouts/MainWrapper'
import { STOCKS, STOCK_ISSUER, STOCK_QUOTE } from 'utils/stocks/catalogue'
import { fetchManyPoolStats, type PoolStats } from 'utils/stocks/candles'
import { feedAgeSeconds, isFeedStale } from 'utils/stocks/pricing'
import {
  describePool,
  formatAge,
  formatBps,
  formatPercent,
  formatUsd,
  formatUsdCompact,
  swapHref
} from './format'
import { useStockMarkets } from './useStockMarkets'
import { Skeleton, StockIcon, StocksJsonLd, Tone } from './shared'

/**
 * `/stocks` — the board.
 *
 * Every number on it is read from Base when the page opens and again every
 * twenty seconds: the price a pool would actually give, the price Chainlink
 * last published for the same share, and the distance between them. The last
 * of those is the column no aggregator shows and the one that says whether
 * this venue is worth trading on right now.
 */

const Page = styled(Flex)`
  width: 100%;
  flex-direction: column;
  margin: 140px 0 96px;
`

const Lead = styled(Text)`
  max-width: 760px;
  margin-top: 12px;
  color: ${({ theme }) => theme.colors.gray3};
`

const Summary = styled(Flex)`
  gap: 12px;
  margin-top: 28px;
  flex-wrap: wrap;
`

const Stat = styled(Flex)`
  flex-direction: column;
  gap: 4px;
  padding: 14px 18px;
  min-width: 168px;
  /* Two per row on a 375px screen: at a 168px minimum they miss by a hair. */
  flex: 1 1 140px;

  ${({ theme }) => theme.mediaQueries.mobile} {
    min-width: 140px;
    padding: 12px 14px;
  }
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.gray11};
`

const StatValue = styled.span`
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gray1};
  font-variant-numeric: tabular-nums;
`

const StatLabel = styled.span`
  font-size: 11px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.gray6};
`

const Scroller = styled.div`
  width: 100%;
  margin-top: 24px;
  overflow-x: auto;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  border-radius: 14px;
  background: ${({ theme }) => theme.colors.gray11};
`

const Board = styled.table`
  width: 100%;
  min-width: 900px;
  border-collapse: collapse;
  font-variant-numeric: tabular-nums;

  th,
  td {
    padding: 12px 16px;
    text-align: right;
    white-space: nowrap;
    border-bottom: 1px solid ${({ theme }) => theme.colors.gray9};
  }

  th {
    position: sticky;
    top: 0;
    z-index: 1;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.gray6};
    background: ${({ theme }) => theme.colors.gray10};
  }

  td {
    font-size: 14px;
    color: ${({ theme }) => theme.colors.gray1};
  }

  th:first-child,
  td:first-child {
    text-align: left;
    position: sticky;
    left: 0;
    background: ${({ theme }) => theme.colors.gray11};
  }

  th:first-child {
    background: ${({ theme }) => theme.colors.gray10};
  }

  /*
   * The buy button is pinned to the right edge for the same reason the company
   * is pinned to the left: this table scrolls sideways on anything narrower
   * than a laptop, and a call to action that scrolls out of the frame is a call
   * to action most people never see.
   */
  th:last-child,
  td:last-child {
    position: sticky;
    right: 0;
    background: ${({ theme }) => theme.colors.gray11};
    border-left: 1px solid ${({ theme }) => theme.colors.gray9};
  }

  th:last-child {
    background: ${({ theme }) => theme.colors.gray10};
  }

  tbody tr:hover td:first-child,
  tbody tr:hover td:last-child {
    background: ${({ theme }) => theme.colors.gray10};
  }

  tbody tr:hover td {
    background: ${({ theme }) => theme.colors.gray10};
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
`

const Company = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  color: inherit;
  min-height: 44px;
`

const Ticker = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gray1};
`

const CompanyName = styled.span`
  display: block;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.gray6};
`

const TradeLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 36px;
  padding: 0 16px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.cyan};
  color: ${({ theme }) => theme.colors.cyan};
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;

  &:hover {
    background: ${({ theme }) => theme.colors.cyan};
    color: ${({ theme }) => theme.colors.black};
  }
`

const Muted = styled.span`
  color: ${({ theme }) => theme.colors.gray6};
`

const Footnote = styled(Text)`
  margin-top: 20px;
  max-width: 820px;
  color: ${({ theme }) => theme.colors.gray6};
  font-size: 12px;
  line-height: 1.6;
`

const TITLE = 'Tokenized stocks on Base — live DEX prices | MPRO Lab'
const DESCRIPTION =
  'Live on-chain prices for Coinbase tokenized stocks on Base: AAPLc, NVDAc, TSLAc and more, each shown next to its Chainlink oracle price with the premium between them, pool depth and a one-click swap.'

const Stocks: React.FC = () => {
  const theme = useTheme()
  const { markets, failed, readAt } = useStockMarkets()
  /** Nothing has been read yet — every number on the page is still a question. */
  const [stats, setStats] = useState<Map<string, PoolStats>>(new Map())
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [])

  const pending = markets.length === 0 && !failed
  const quotingPools = markets.map((m) => m.quotingPool?.address).filter(Boolean) as string[]
  const poolKey = quotingPools.join(',')

  useEffect(() => {
    if (!poolKey) return undefined
    const controller = new AbortController()
    void fetchManyPoolStats({ pools: poolKey.split(','), signal: controller.signal }).then((next) => {
      // An index that rate-limited us answers with nothing. Keeping the last
      // good reading beats blanking a column that was right a minute ago.
      if (!controller.signal.aborted && next.size > 0) setStats(next)
    })
    return () => controller.abort()
  }, [poolKey])

  const totals = useMemo(() => {
    const traded = markets.filter((m) => m.quotingPool !== null)
    const liquidity = markets.reduce((sum, m) => sum + m.liquidityUsd, 0)
    const volume = traded.reduce(
      (sum, m) => sum + (stats.get(m.quotingPool?.address ?? '')?.volume24hUsd ?? 0),
      0
    )
    return { traded: traded.length, liquidity, volume }
  }, [markets, stats])

  return (
    <>
      <Helmet>
        <title>{TITLE}</title>
        <meta name="description" content={DESCRIPTION} />
        <link rel="canonical" href="https://mprolab.io/stocks" />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:url" content="https://mprolab.io/stocks" />
      </Helmet>
      <StocksJsonLd />
      <MainWrapper>
        <Page>
          <Text variant={TextVariant.H4} color={theme.colors.gray1}>
            Tokenized stocks on Base
          </Text>
          <Lead variant={TextVariant.BODY_SMALL}>
            {STOCKS.length} US equities issued onchain by {STOCK_ISSUER.name} as B20 tokens, traded in
            {' '}{STOCK_QUOTE.symbol} pools on Base. Every price below is read from the pools themselves, next
            to the Chainlink price for the same share — so you can see what the market is charging over the
            oracle before you trade, not after.
          </Lead>

          <Summary>
            <Stat>
              <StatValue>
                {pending ? <Skeleton $width="84px" $height="20px" /> : `${totals.traded} / ${STOCKS.length}`}
              </StatValue>
              <StatLabel>With a live pool</StatLabel>
            </Stat>
            <Stat>
              <StatValue>
                {pending ? <Skeleton $width="96px" $height="20px" /> : formatUsdCompact(totals.liquidity)}
              </StatValue>
              <StatLabel>Pool depth on Base</StatLabel>
            </Stat>
            <Stat>
              <StatValue>
                {pending || (totals.volume === 0 && stats.size === 0) ? (
                  <Skeleton $width="96px" $height="20px" />
                ) : (
                  formatUsdCompact(totals.volume)
                )}
              </StatValue>
              <StatLabel>24 h volume</StatLabel>
            </Stat>
            <Stat>
              <StatValue>
                {failed ? (
                  'stale'
                ) : readAt ? (
                  `${Math.max(0, Math.round((now - readAt) / 1000))}s`
                ) : (
                  <Skeleton $width="48px" $height="20px" />
                )}
              </StatValue>
              <StatLabel>{failed ? 'Chain unreachable' : 'Read from Base'}</StatLabel>
            </Stat>
          </Summary>

          <Scroller>
            <Board>
              <thead>
                <tr>
                  <th scope="col">Company</th>
                  <th scope="col">Pool price</th>
                  <th scope="col">24 h</th>
                  <th scope="col">Oracle</th>
                  <th scope="col">Premium</th>
                  <th scope="col">Depth</th>
                  <th scope="col">Venue</th>
                  <th scope="col">Buy</th>
                </tr>
              </thead>
              <tbody>
                {/*
                  The company column is known before any read — it is a
                  catalogue, not a price — so the table opens at its final shape
                  and only the cells that are being read shimmer. The rows do not
                  move when the numbers land.
                */}
                {markets.length === 0 &&
                  STOCKS.map((stock) => (
                    <tr key={stock.slug}>
                      <td>
                        <Company to={`/stocks/${stock.slug}`}>
                          <StockIcon src={stock.icon} alt="" />
                          <span>
                            <Ticker>{stock.ticker}</Ticker>
                            <CompanyName>{stock.name}</CompanyName>
                          </span>
                        </Company>
                      </td>
                      {failed ? (
                        <td colSpan={7}>
                          <Muted>Base could not be read just now</Muted>
                        </td>
                      ) : (
                        [72, 52, 72, 56, 64, 148, 64].map((width, index) => (
                          <td key={width + index}>
                            <Skeleton $width={`${width}px`} />
                          </td>
                        ))
                      )}
                    </tr>
                  ))}
                {markets.map((market) => {
                  const pool = market.quotingPool
                  const change = stats.get(pool?.address ?? '')?.change24hPercent ?? null
                  const age = feedAgeSeconds(market.oracleUpdatedAt, now)
                  return (
                    <tr key={market.stock.slug}>
                      <td>
                        <Company to={`/stocks/${market.stock.slug}`}>
                          <StockIcon src={market.stock.icon} alt="" />
                          <span>
                            <Ticker>{market.stock.ticker}</Ticker>
                            <CompanyName>{market.stock.name}</CompanyName>
                          </span>
                        </Company>
                      </td>
                      <td>{market.poolPrice === null ? <Muted>no pool</Muted> : formatUsd(market.poolPrice)}</td>
                      <td>
                        {/* The chain answers in one round trip; the index is a
                            separate request, so this cell keeps its own state. */}
                        {change === null && stats.size === 0 && pool ? (
                          <Skeleton $width="52px" />
                        ) : (
                          <Tone $value={change}>{formatPercent(change)}</Tone>
                        )}
                      </td>
                      <td>
                        {formatUsd(market.oraclePrice)}
                        <CompanyName>
                          {isFeedStale(market.oracleUpdatedAt, now) ? 'stale · ' : ''}
                          {formatAge(age)}
                        </CompanyName>
                      </td>
                      <td>
                        <Tone $value={market.premiumBps === null ? null : -market.premiumBps}>
                          {formatBps(market.premiumBps)}
                        </Tone>
                      </td>
                      <td>{formatUsdCompact(market.liquidityUsd)}</td>
                      <td>
                        <Muted>{describePool(pool)}</Muted>
                      </td>
                      <td>
                        {pool ? (
                          <TradeLink
                            to={swapHref(market.stock.address, STOCK_QUOTE.address)}
                            aria-label={`Buy ${market.stock.symbol} with ${STOCK_QUOTE.symbol}`}
                          >
                            Buy
                          </TradeLink>
                        ) : (
                          <Muted>not traded yet</Muted>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Board>
          </Scroller>

          <Footnote variant={TextVariant.BODY_SMALL}>
            Pool price is what {STOCK_QUOTE.symbol} would actually buy in the deepest pool that answered, read
            through the venue&apos;s own quoter. Oracle is the issuer&apos;s Chainlink feed for the underlying
            share — it runs 24/5, holds the last close at weekends, and freezes during corporate actions, so an
            old reading is usually a closed market rather than a fault. Premium is the pool above the oracle;
            negative means the pool is cheaper. 24 h change and volume come from GeckoTerminal&apos;s index of
            the same pool. These tokens carry issuer controls — a wallet can be blocked and its balance burned;
            read <Link to="/documentation/tokenized-stocks">how tokenized stocks work</Link> before you trade.
          </Footnote>
        </Page>
      </MainWrapper>
    </>
  )
}

export default Stocks
